import { useCallback } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  updateExperimentRolloutSchema,
  type UpdateExperimentRolloutFormValues,
} from "@/lib/validation/experiment-schema";
import { useUpdateExperimentRollout } from "@/hooks/use-experiments";
import type { Experiment } from "@/types/experiment";

interface RolloutExperimentDialogProps {
  experiment: Experiment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RolloutExperimentDialog({
  experiment,
  open,
  onOpenChange,
}: RolloutExperimentDialogProps) {
  const rolloutMutation = useUpdateExperimentRollout();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateExperimentRolloutFormValues>({
    resolver: zodResolver(updateExperimentRolloutSchema),
    defaultValues: {
      variants: experiment?.variants ?? [],
    },
  });

  const variants = useWatch({ control, name: "variants" });
  const totalWeight =
    variants?.reduce((sum, variant) => sum + (variant.weight || 0), 0) ?? 0;

  const onSubmit = useCallback(
    (data: UpdateExperimentRolloutFormValues) => {
      if (!experiment) return;

      rolloutMutation.mutate(
        {
          key: experiment.key,
          variants: experiment.variants.map((variant, index) => ({
            key: variant.key,
            weight: data.variants[index]?.weight ?? 0,
          })),
        },
        {
          onSuccess: () => onOpenChange(false),
        },
      );
    },
    [experiment, rolloutMutation, onOpenChange],
  );

  if (!experiment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>rollout 조정</DialogTitle>
          <DialogDescription>
            variant key는 유지하고 노출 비율만 변경합니다.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>실험 Key</Label>
            <code className="block rounded bg-muted px-3 py-2 text-sm font-mono">
              {experiment.key}
            </code>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Variants</Label>
              <span
                className={`text-xs ${totalWeight === 10000 ? "text-emerald-600" : "text-destructive"}`}
              >
                합계: {(totalWeight / 100).toFixed(1)}%
              </span>
            </div>

            {experiment.variants.map((variant, index) => (
              <div
                key={variant.key}
                className="grid grid-cols-[minmax(0,1fr)_96px_52px] items-start gap-2"
              >
                <div className="min-w-0">
                  <code className="block truncate rounded bg-muted px-3 py-2 text-sm font-mono">
                    {variant.key}
                  </code>
                </div>
                <div>
                  <Controller
                    name={`variants.${index}.weight`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        min={0}
                        max={10000}
                        step={1}
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(Number(event.target.value) || 0)
                        }
                      />
                    )}
                  />
                  {errors.variants?.[index]?.weight && (
                    <p className="mt-1 text-xs text-destructive">
                      {errors.variants[index].weight.message}
                    </p>
                  )}
                </div>
                <div className="pt-2 text-right text-xs text-muted-foreground">
                  {`${((variants?.[index]?.weight ?? 0) / 100).toFixed(1)}%`}
                </div>
              </div>
            ))}

            {errors.variants?.root && (
              <p className="text-sm text-destructive">
                {errors.variants.root.message}
              </p>
            )}
            {errors.variants?.message && (
              <p className="text-sm text-destructive">
                {errors.variants.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={rolloutMutation.isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={rolloutMutation.isPending}>
              적용
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
